import { CapacitorHttp } from '@capacitor/core';
import { recordNativeDiagnostic } from './nativeDiagnostics';
import { isNativeApp } from './nativePlatform';

// Status codes that must have a null body when constructing a Response.
const NULL_BODY_STATUS = new Set([101, 204, 205, 304]);

/**
 * Issue an HTTP request that works correctly inside the iOS Capacitor WebView.
 *
 * On native platforms the WebView intercepts in-page `fetch()` calls to remote
 * origins, so requests to the backend never leave the device (surfacing as
 * `TypeError: Load failed`). Routing backend calls through the native
 * CapacitorHttp plugin (URLSession) bypasses the WebView entirely and is not
 * subject to browser CORS. On web we use the standard `fetch`.
 *
 * Returns a real `Response` so existing callers keep working unchanged.
 */
export async function httpFetch(url: string, init: RequestInit = {}): Promise<Response> {
  if (!isNativeApp()) {
    return fetch(url, init);
  }

  const method = (init.method || 'GET').toUpperCase();
  const headers = { ...(init.headers as Record<string, string> | undefined) };

  // Our callers always send a JSON string body (or none). CapacitorHttp expects
  // the unserialized value for JSON requests, so parse it back when possible.
  let data: unknown;
  if (init.body != null && typeof init.body === 'string') {
    const contentType = headers['Content-Type'] || headers['content-type'] || '';
    if (contentType.includes('application/json')) {
      try {
        data = JSON.parse(init.body);
      } catch {
        data = init.body;
      }
    } else {
      data = init.body;
    }
  } else if (init.body != null) {
    data = init.body;
  }

  const native = await CapacitorHttp.request({ url, method, headers, data });
  const status = native.status;
  const responseHeaders = (native.headers || {}) as Record<string, string>;
  const bodyText =
    native.data == null
      ? ''
      : typeof native.data === 'string'
        ? native.data
        : JSON.stringify(native.data);

  recordNativeDiagnostic('Native HTTP response', { url, status });

  return new Response(NULL_BODY_STATUS.has(status) ? null : bodyText, {
    status,
    headers: responseHeaders
  });
}
